import React, { useEffect, useState, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
  Modal,
  RefreshControl } from 'react-native'
import {
  ArrowLeft,
  ClipboardList,
  Pencil,
  Zap,
  Undo2,
  Package,
  Coins,
  BarChart3,
  AlertTriangle,
  ChevronRight,
  X,
  type LucideIcon } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StockAdjustment'>
  route: RouteProp<RootStackParamList, 'StockAdjustment'>
}

interface Product {
  id: string
  title: string
  sku: string | null
  barcode: string | null
  stock_quantity: number
  low_stock_threshold: number
  price: number | null
}

interface StockMovement {
  id: string
  product_id: string
  movement_type: string
  quantity_change: number
  quantity_before: number
  quantity_after: number
  reason: string | null
  created_at: string
  product?: { title: string }
}

type AdjustmentType = 'count' | 'adjustment' | 'damage' | 'return'

const ADJUSTMENT_TYPES: { value: AdjustmentType; label: string; labelFi: string; icon: LucideIcon }[] = [
  { value: 'count', label: 'Physical Count', labelFi: 'Inventaario', icon: ClipboardList },
  { value: 'adjustment', label: 'Adjustment', labelFi: 'Korjaus', icon: Pencil },
  { value: 'damage', label: 'Damage/Loss', labelFi: 'Vaurio/Hävikki', icon: Zap },
  { value: 'return', label: 'Customer Return', labelFi: 'Palautus', icon: Undo2 },
]

const MOVEMENT_ICONS: Record<string, LucideIcon> = {
  count: ClipboardList,
  adjustment: Pencil,
  damage: Zap,
  return: Undo2,
  purchase: Package,
  sale: Coins }

export default function StockAdjustmentScreen({ navigation, route }: Props) {
  const { storeId, productId: preselectedProductId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'adjust' | 'history'>('adjust')
  const [products, setProducts] = useState<Product[]>([])
  const [movements, setMovements] = useState<StockMovement[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [search, setSearch] = useState('')

  // Adjustment modal state
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('count')
  const [newQuantity, setNewQuantity] = useState('')
  const [quantityChange, setQuantityChange] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchProducts()
    fetchMovements()
  }, [storeId])

  useEffect(() => {
    // Auto-open modal if product was preselected
    if (preselectedProductId && products.length > 0) {
      const product = products.find(p => p.id === preselectedProductId)
      if (product) {
        openAdjustModal(product)
      }
    }
  }, [preselectedProductId, products])

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, title, sku, barcode, stock_quantity, low_stock_threshold, price')
        .eq('flea_market_id', storeId)
        .eq('ownership_type', 'store_owned')
        .in('status', ['for_sale', 'draft'])
        .order('title')

      if (error) throw error
      setProducts(data || [])
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const fetchMovements = async () => {
    try {
      const { data, error } = await supabase
        .from('stock_movements')
        .select(`
          id, product_id, movement_type, quantity_change, quantity_before, quantity_after, reason, created_at,
          product:products(title)
        `)
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setMovements((data || []).map(m => ({
        ...m,
        product: (m.product as any) as { title: string } | undefined })))
    } catch (error) {
      console.error('Error fetching movements:', error)
    }
  }

  const onRefresh = useCallback(() => {
    setRefreshing(true)
    fetchProducts()
    fetchMovements()
  }, [storeId])

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  const openAdjustModal = (product: Product) => {
    setSelectedProduct(product)
    setNewQuantity(product.stock_quantity.toString())
    setQuantityChange('')
    setReason('')
    setAdjustmentType('count')
    setModalVisible(true)
  }

  const calculateChange = () => {
    if (!selectedProduct) return 0
    if (adjustmentType === 'count') {
      const newQty = parseInt(newQuantity) || 0
      return newQty - selectedProduct.stock_quantity
    } else {
      return parseInt(quantityChange) || 0
    }
  }

  const getNewTotal = () => {
    if (!selectedProduct) return 0
    return selectedProduct.stock_quantity + calculateChange()
  }

  const saveAdjustment = async () => {
    if (!selectedProduct || !user) return

    const change = calculateChange()
    if (change === 0) {
      showAlert(t('error') || 'Error', t('no_change') || 'No quantity change specified')
      return
    }

    const newTotal = getNewTotal()
    if (newTotal < 0) {
      showAlert(t('error') || 'Error', t('negative_stock') || 'Stock cannot be negative')
      return
    }

    setSaving(true)
    try {
      // Call the record_stock_movement function
      const { data, error } = await supabase.rpc('record_stock_movement', {
        p_product_id: selectedProduct.id,
        p_movement_type: adjustmentType,
        p_quantity_change: change,
        p_reference_type: 'manual_adjustment',
        p_reference_id: null,
        p_reason: reason || null,
        p_user_id: user.id })

      if (error) throw error

      setModalVisible(false)
      fetchProducts()
      fetchMovements()

      showAlert(
        t('success') || 'Success',
        t('stock_updated') || `Stock updated: ${selectedProduct.stock_quantity} → ${newTotal}`
      )
    } catch (error: any) {
      console.error('Adjustment error:', error)
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setSaving(false)
    }
  }

  const filteredProducts = products.filter(p => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      p.title?.toLowerCase().includes(searchLower) ||
      p.sku?.toLowerCase().includes(searchLower) ||
      p.barcode?.toLowerCase().includes(searchLower)
    )
  })

  const getMovementIcon = (type: string): LucideIcon => {
    return MOVEMENT_ICONS[type] || BarChart3
  }

  const getMovementLabel = (type: string) => {
    const found = ADJUSTMENT_TYPES.find(t => t.value === type)
    if (found) return language === 'fi' ? found.labelFi : found.label
    switch (type) {
      case 'purchase': return language === 'fi' ? 'Osto' : 'Purchase'
      case 'sale': return language === 'fi' ? 'Myynti' : 'Sale'
      default: return type
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit' })
  }

  const renderProductItem = ({ item }: { item: Product }) => {
    const isLowStock = item.low_stock_threshold > 0 && item.stock_quantity <= item.low_stock_threshold

    return (
      <TouchableOpacity
        style={[styles.productCard, isLowStock && styles.productCardWarning]}
        onPress={() => openAdjustModal(item)}
      >
        <View style={styles.productInfo}>
          <Text style={styles.productTitle} numberOfLines={1}>{item.title}</Text>
          <View style={styles.productMeta}>
            {item.sku && <Text style={styles.productSku}>SKU: {item.sku}</Text>}
            {item.barcode && <Text style={styles.productBarcode}>{item.barcode}</Text>}
          </View>
        </View>
        <View style={styles.stockInfo}>
          <Text style={[styles.stockQuantity, isLowStock && styles.stockQuantityWarning]}>
            {item.stock_quantity}
          </Text>
          <Text style={styles.stockLabel}>{t('in_stock') || 'in stock'}</Text>
          {isLowStock && (
            <View style={styles.lowStockBadge}>
              <AlertTriangle size={12} color="#FF9800" />
              <Text style={styles.lowStockText}>{t('low') || 'Low'}</Text>
            </View>
          )}
        </View>
        <ChevronRight size={16} color={theme.colors.text.secondary} />
      </TouchableOpacity>
    )
  }

  const renderMovementItem = ({ item }: { item: StockMovement }) => {
    const MovementIcon = getMovementIcon(item.movement_type)
    return (
    <View style={styles.movementCard}>
      <View style={styles.movementIconContainer}>
        <MovementIcon size={20} color={merchantColors.primary} />
      </View>
      <View style={styles.movementInfo}>
        <Text style={styles.movementTitle} numberOfLines={1}>
          {item.product?.title || 'Unknown Product'}
        </Text>
        <Text style={styles.movementType}>{getMovementLabel(item.movement_type)}</Text>
        {item.reason && <Text style={styles.movementReason}>{item.reason}</Text>}
        <Text style={styles.movementDate}>{formatDate(item.created_at)}</Text>
      </View>
      <View style={styles.movementChange}>
        <Text style={[
          styles.changeAmount,
          { color: item.quantity_change > 0 ? '#4CAF50' : '#F44336' }
        ]}>
          {item.quantity_change > 0 ? '+' : ''}{item.quantity_change}
        </Text>
        <Text style={styles.changeResult}>
          {item.quantity_before} → {item.quantity_after}
        </Text>
      </View>
    </View>
    )
  }

  const renderAdjustModal = () => (
    <Modal visible={modalVisible} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('adjust_stock') || 'Adjust Stock'}</Text>
            <TouchableOpacity onPress={() => setModalVisible(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>

          {selectedProduct && (
            <View style={styles.modalBody}>
              {/* Product info */}
              <View style={styles.selectedProductInfo}>
                <Text style={styles.selectedProductTitle}>{selectedProduct.title}</Text>
                <Text style={styles.selectedProductStock}>
                  {t('current_stock') || 'Current'}: <Text style={styles.currentStockValue}>{selectedProduct.stock_quantity}</Text>
                </Text>
              </View>

              {/* Adjustment type */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('adjustment_type') || 'Adjustment Type'}</Text>
                <View style={styles.typeButtons}>
                  {ADJUSTMENT_TYPES.map(type => {
                    const TypeIcon = type.icon
                    return (
                    <TouchableOpacity
                      key={type.value}
                      style={[styles.typeButton, adjustmentType === type.value && styles.typeButtonActive]}
                      onPress={() => setAdjustmentType(type.value)}
                    >
                      <View style={styles.typeButtonIconContainer}>
                        <TypeIcon size={16} color={adjustmentType === type.value ? merchantColors.primary : theme.colors.text.secondary} />
                      </View>
                      <Text style={[styles.typeButtonText, adjustmentType === type.value && styles.typeButtonTextActive]}>
                        {language === 'fi' ? type.labelFi : type.label}
                      </Text>
                    </TouchableOpacity>
                    )
                  })}
                </View>
              </View>

              {/* Quantity input - varies by type */}
              {adjustmentType === 'count' ? (
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>{t('actual_count') || 'Actual Count'}</Text>
                  <TextInput
                    style={styles.input}
                    value={newQuantity}
                    onChangeText={setNewQuantity}
                    placeholder="0"
                    placeholderTextColor={theme.colors.text.secondary}
                    keyboardType="number-pad"
                  />
                  <Text style={styles.hint}>
                    {t('count_hint') || 'Enter the actual quantity you counted'}
                  </Text>
                </View>
              ) : (
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>
                    {adjustmentType === 'return'
                      ? (t('quantity_returned') || 'Quantity Returned')
                      : (t('quantity_change') || 'Quantity Change')}
                  </Text>
                  <View style={styles.changeInputRow}>
                    <TouchableOpacity
                      style={styles.changeButton}
                      onPress={() => setQuantityChange(prev => {
                        const val = parseInt(prev) || 0
                        return (val - 1).toString()
                      })}
                    >
                      <Text style={styles.changeButtonText}>−</Text>
                    </TouchableOpacity>
                    <TextInput
                      style={[styles.input, styles.changeInput]}
                      value={quantityChange}
                      onChangeText={setQuantityChange}
                      placeholder="0"
                      placeholderTextColor={theme.colors.text.secondary}
                      keyboardType="number-pad"
                    />
                    <TouchableOpacity
                      style={styles.changeButton}
                      onPress={() => setQuantityChange(prev => {
                        const val = parseInt(prev) || 0
                        return (val + 1).toString()
                      })}
                    >
                      <Text style={styles.changeButtonText}>+</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.hint}>
                    {adjustmentType === 'damage'
                      ? (t('damage_hint') || 'Enter negative for items lost/damaged')
                      : adjustmentType === 'return'
                      ? (t('return_hint') || 'Enter positive for items returned')
                      : (t('adjustment_hint') || 'Positive to add, negative to remove')}
                  </Text>
                </View>
              )}

              {/* Preview */}
              <View style={styles.preview}>
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>{t('before') || 'Before'}:</Text>
                  <Text style={styles.previewValue}>{selectedProduct.stock_quantity}</Text>
                </View>
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>{t('change') || 'Change'}:</Text>
                  <Text style={[
                    styles.previewValue,
                    { color: calculateChange() > 0 ? '#4CAF50' : calculateChange() < 0 ? '#F44336' : theme.colors.text.secondary }
                  ]}>
                    {calculateChange() > 0 ? '+' : ''}{calculateChange()}
                  </Text>
                </View>
                <View style={[styles.previewRow, styles.previewTotal]}>
                  <Text style={styles.previewLabel}>{t('after') || 'After'}:</Text>
                  <Text style={[styles.previewValue, styles.previewTotalValue]}>
                    {getNewTotal()}
                  </Text>
                </View>
              </View>

              {/* Reason */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('reason') || 'Reason (optional)'}</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={reason}
                  onChangeText={setReason}
                  placeholder={t('reason_placeholder') || 'Why is this adjustment being made?'}
                  placeholderTextColor={theme.colors.text.secondary}
                  multiline
                  numberOfLines={2}
                />
              </View>
            </View>
          )}

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.saveButton, (saving || calculateChange() === 0) && styles.buttonDisabled]}
              onPress={saveAdjustment}
              disabled={saving || calculateChange() === 0}
            >
              {saving ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>{t('save_adjustment') || 'Save Adjustment'}</Text>
              )}
            </TouchableOpacity>

            <Button variant="outline" onPress={() => setModalVisible(false)}><ButtonText>{t('cancel') || 'Cancel'}</ButtonText></Button>
          </View>
        </View>
      </View>
    </Modal>
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButtonContainer}>
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text style={styles.backButton}>{t('back') || 'Back'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('stock_adjustment') || 'Stock Adjustment'}</Text>
        <View style={{ width: 60 }} />
      </View>

      {/* Tabs */}
      <View style={styles.tabs}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'adjust' && styles.tabActive]}
          onPress={() => setActiveTab('adjust')}
        >
          <Text style={[styles.tabText, activeTab === 'adjust' && styles.tabTextActive]}>
            {t('adjust') || 'Adjust'}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'history' && styles.tabActive]}
          onPress={() => setActiveTab('history')}
        >
          <Text style={[styles.tabText, activeTab === 'history' && styles.tabTextActive]}>
            {t('history') || 'History'}
          </Text>
        </TouchableOpacity>
      </View>

      {activeTab === 'adjust' ? (
        <>
          {/* Search */}
          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder={t('search_products') || 'Search by name, SKU, or barcode...'}
              placeholderTextColor={theme.colors.text.secondary}
            />
            {search.length > 0 && (
              <TouchableOpacity style={styles.clearSearch} onPress={() => setSearch('')}>
                <X size={18} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            )}
          </View>

          {/* Products list */}
          <FlatList
            data={filteredProducts}
            renderItem={renderProductItem}
            keyExtractor={item => item.id}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />
            }
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <View style={styles.emptyIconContainer}>
                  <Package size={48} color={theme.colors.text.secondary} />
                </View>
                <Text style={styles.emptyText}>
                  {search ? (t('no_results') || 'No products found') : (t('no_store_products') || 'No store-owned products')}
                </Text>
              </View>
            }
          />
        </>
      ) : (
        <FlatList
          data={movements}
          renderItem={renderMovementItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <View style={styles.emptyIconContainer}>
                <BarChart3 size={48} color={theme.colors.text.secondary} />
              </View>
              <Text style={styles.emptyText}>{t('no_movements') || 'No stock movements yet'}</Text>
            </View>
          }
        />
      )}

      {renderAdjustModal()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center' },
  backButton: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  tabs: {
    flexDirection: 'row',
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  tab: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent' },
  tabActive: {
    borderBottomColor: merchantColors.primary },
  tabText: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  tabTextActive: {
    color: merchantColors.primary,
    fontWeight: '600' },
  searchContainer: {
    backgroundColor: theme.colors.background.card,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center' },
  searchInput: {
    flex: 1,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: 10,
    fontSize: 15,
    color: theme.colors.text.primary },
  clearSearch: {
    position: 'absolute',
    right: 24,
    padding: theme.spacing.sm },
  listContent: {
    padding: theme.spacing.md,
    paddingBottom: 120 },
  productCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    marginBottom: 10,
    ...theme.shadows.small },
  productCardWarning: {
    borderLeftWidth: 4,
    borderLeftColor: '#FF9800' },
  productInfo: {
    flex: 1 },
  productTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  productMeta: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4 },
  productSku: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  productBarcode: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' },
  stockInfo: {
    alignItems: 'center',
    marginHorizontal: 12 },
  stockQuantity: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  stockQuantityWarning: {
    color: '#FF9800' },
  stockLabel: {
    fontSize: 10,
    color: theme.colors.text.secondary },
  lowStockBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2 },
  lowStockText: {
    fontSize: 10,
    color: '#FF9800',
    marginLeft: 2 },
  movementCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    marginBottom: 10,
    ...theme.shadows.small },
  movementIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12 },
  movementInfo: {
    flex: 1 },
  movementTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  movementType: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  movementReason: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    fontStyle: 'italic',
    marginTop: 2 },
  movementDate: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    marginTop: 4 },
  movementChange: {
    alignItems: 'flex-end' },
  changeAmount: {
    fontSize: 18,
    fontWeight: 'bold' },
  changeResult: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: theme.spacing.xl },
  emptyIconContainer: {
    marginBottom: theme.spacing.md },
  emptyText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '90%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  modalCloseButton: {
    padding: 4 },
  modalBody: {
    padding: theme.spacing.md },
  selectedProductInfo: {
    backgroundColor: theme.colors.background.secondary,
    padding: 14,
    borderRadius: theme.borderRadius.md,
    marginBottom: 20 },
  selectedProductTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  selectedProductStock: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginTop: 4 },
  currentStockValue: {
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  inputGroup: {
    marginBottom: 20 },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  textArea: {
    height: 60,
    textAlignVertical: 'top' },
  hint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 6 },
  typeButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8 },
  typeButton: {
    flex: 1,
    minWidth: '45%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  typeButtonActive: {
    borderColor: merchantColors.primary,
    backgroundColor: `${merchantColors.primary}15` },
  typeButtonIconContainer: {
    marginRight: 6 },
  typeButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  typeButtonTextActive: {
    color: merchantColors.primary },
  changeInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12 },
  changeButton: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: merchantColors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center' },
  changeButtonText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff' },
  changeInput: {
    flex: 1,
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 'bold' },
  preview: {
    backgroundColor: '#E3F2FD',
    borderRadius: theme.borderRadius.md,
    padding: 14,
    marginBottom: 20 },
  previewRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4 },
  previewLabel: {
    fontSize: 14,
    color: '#1976D2' },
  previewValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1976D2' },
  previewTotal: {
    borderTopWidth: 1,
    borderTopColor: '#90CAF9',
    marginTop: 8,
    paddingTop: 8 },
  previewTotalValue: {
    fontSize: 18,
    fontWeight: 'bold' },
  modalActions: {
    padding: theme.spacing.md,
    paddingBottom: 30,
    gap: 12 },
  saveButton: {
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center' },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  cancelButton: {
    padding: 12,
    alignItems: 'center' },
  cancelButtonText: {
    color: theme.colors.text.secondary,
    fontSize: 16 },
  buttonDisabled: {
    opacity: 0.6 } })
